import type { MetaDescriptor } from 'react-router';
import { getNameFromSearch } from '~/scripts/getNameFromSearch';
import { getPageMetaTags } from '~/scripts/getPageMetaTags';
import { isSingleReportSearch } from '~/scripts/isSingleReportSearch';
import type { Route } from '../../routes/+types/reports_.$name.($uuid)';
import { getReportImage } from '~/scripts/getReportImage';
import getFirstReportImage from '~/scripts/getFirstReportImage';

export const meta: Route.MetaFunction = ({
  loaderData,
  error,
  params,
}: Route.MetaArgs) => {
  if (error) {
    return [];
  }
  if (!loaderData?.search) {
    return [];
  }
  if (params.name === 'new' && !params.uuid) {
    return getPageMetaTags({
      title: `New Code Search`,
      description: `Create a new website list by searching for technologies, HTML, or Javascript.`,
      canonical: 'https://nerdydata.com/reports/new',
      breadcrumbs: loaderData?.breadcrumbs,
    });
  }
  const singleReport = isSingleReportSearch(loaderData?.search);
  const name = getNameFromSearch(loaderData?.search || [], []);
  const reportImage = getReportImage(
    getFirstReportImage(loaderData?.search || {}) || '0'
  );
  const description = (function () {
    if (singleReport && loaderData?.report) {
      return `${name} is ${
        /^[aeiou]/i.test(loaderData.report.category) ? 'an' : 'a'
      } ${loaderData.report.category} technology used on ${
        loaderData.total ? loaderData.total.toLocaleString() : ''
      } websites. `;
    }
    if (!loaderData?.total || !loaderData?.sites) {
      return `Download a list of website using ${name}, including company, social, and technographic data for each site.`;
    }

    return `Download a list of ${loaderData.total.toLocaleString()} websites that use ${name}. Example sites include: ${loaderData.sites
      .slice(0, 3)
      .map((site) => site.domain)
      .join(', ')}.`;
  })();
  const title = `${
    loaderData?.total ? loaderData.total.toLocaleString() : ''
  } Websites using ${getNameFromSearch(loaderData?.search || [], [])}`;

  const canonical = `${import.meta.env.VITE_ORIGIN}${loaderData?.url}`;

  const metaTags: MetaDescriptor[] = getPageMetaTags({
    title,
    description,
    image: reportImage,
    canonical,
    breadcrumbs: loaderData?.breadcrumbs,
    faq: [
      {
        question: `Which websites use ${name} in their code, and how many in total?`,
        answer: `There are ${loaderData?.total?.toLocaleString()} websites that are built using ${name} (found in their HTML or Javascript). NerdyData.com is a search engine for source code which identifies technologies used on millions of websites.`,
      },
    ],
  });

  metaTags.push({
    'script:ld+json': {
      '@context': 'https://schema.org',
      '@type': 'QAPage',
      name: 'NerdyData.com Results Q&A',
      mainEntity: {
        '@type': 'Question',
        name: `How many websites use ${name}?`,
        text: `Which websites are built using ${name} in their source code?`,
        answerCount: loaderData?.total,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${loaderData?.total?.toLocaleString()} websites are actively using ${name}`,
          url: `${canonical}?utm_source=ldjson-QAPage`,
        },
      },
    },
  });

  if (singleReport && loaderData?.report) {
    metaTags.push({
      'script:ld+json': {
        '@context': 'https://schema.org/',
        '@type': 'Dataset',
        license: 'https://creativecommons.org/publicdomain/zero/1.0/',
        name: `Websites built with ${name}`,
        description: `Download ${loaderData?.total?.toLocaleString()} websites that are built using ${
          loaderData?.report?.name
        } HTML or Javascript. NerdyData.com is a search engine for source code which identifies technologies used on millions of websites.\n\n<img src=&quot;https://static.nerdydata.com/reports/${
          loaderData?.report?.uuid
        }.jpg&quot; />`,
        url: `${canonical}?utm_source=ldjson-Dataset`,
        keywords: [
          `Website Technologies > ${loaderData?.report?.category} > ${loaderData?.report?.name}`,
        ],
        isAccessibleForFree: true,
        creator: {
          '@type': 'Organization',
          url: 'https://www.nerdydata.com/',
          name: 'NerdyData.com',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'support+datasets@nerdydata.com',
          },
        },
        dateModified: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
        distribution: [
          {
            '@type': 'DataDownload',
            encodingFormat: 'CSV',
            contentUrl: `${canonical}?utm_source=ldjson-Dataset`,
            description: `CSV file containing ${loaderData?.total?.toLocaleString()} websites that are built using ${
              loaderData?.report?.name
            } HTML or Javascript`,
            datePublished: new Date(
              new Date().setHours(0, 0, 0, 0)
            ).toISOString(),
          },
        ],
      },
    });

    metaTags.push({
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: loaderData.report.name,
        applicationCategory: loaderData.report.category,
        url: canonical,
        ...(loaderData.reportDetails?.link
          ? { sameAs: loaderData.reportDetails.link }
          : {}),
        description: `${loaderData.report.name} is a ${loaderData.report.category} technology used on ${loaderData.total?.toLocaleString()} websites. NerdyData tracks its adoption across millions of crawled domains.`,
        dateModified: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
      },
    });
  }
  return metaTags;
};
